import { Divider, Group, Select } from '@byecode/ui'
import {
    emptyTextField,
    type FlowNode,
    getInputInnerTypeMatch,
    getUpstreamNodesOptions,
    ListItem4ByecodeUi,
    NodeFieldPreview,
    nodeTypeOptions,
    type NodeTypes,
    OtherPlatformNodeType,
    OUTPUT_SINGLE_DATA_NODES,
    useAtomData,
    type WechatFollowWaybillActionConfig
} from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import OutputVariable from '../../../OutputVariable'
import { UnitNoAuth } from '../../Common/UnitNoAuth'
import * as CM from '../../styles'
import { SelectMultipleRecord } from '../component/SelectMultipleRecord'
import { DeliveryCompanyField } from '../wechatShipping/DeliveryCompanyField'

interface WechatFollowWaybillActionConfigureProps {
    nodeType?: NodeTypes
    allParentNodes: FlowNode[]
    actionTypeSwitcher?: React.ReactNode
    value: WechatFollowWaybillActionConfig
    onChange: (value: WechatFollowWaybillActionConfig) => void
}

export const WechatFollowWaybillActionConfigure: React.FC<WechatFollowWaybillActionConfigureProps> = ({
    nodeType,
    allParentNodes,
    actionTypeSwitcher,
    value,
    onChange
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const disabledWithVersion = useIsDisabledWithVersion()
    const otherPlatformList = useAtomData(otherPlatformListAtom)
    const userOption = useGetUserRecordOptions()
    const miniProgramAccount = useMemo(
        () => otherPlatformList?.find(item => item.type === 'WECHAT_MINI_PROGRAM'),
        [otherPlatformList]
    )

    const singleRecordNodes = useMemo(
        () => allParentNodes.filter(node => OUTPUT_SINGLE_DATA_NODES.has(node.data.nodeType)),
        [allParentNodes]
    )

    const goodsFieldNodes = useMemo(
        () =>
            allParentNodes.filter(node => OUTPUT_SINGLE_DATA_NODES.has(node.data.nodeType) || node.id === value.goodsListNodeId),
        [allParentNodes, value.goodsListNodeId]
    )

    const textUpstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: singleRecordNodes,
                allUpstreamNodes: allParentNodes
            }),
        [allParentNodes, dataSourceList, singleRecordNodes, workflowList]
    )

    const goodsTextUpstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: goodsFieldNodes,
                allUpstreamNodes: allParentNodes
            }),
        [allParentNodes, dataSourceList, goodsFieldNodes, workflowList]
    )

    const header = (
        <Group label="动作配置">
            <ListItem4ByecodeUi>
                <CM.FormItemLabelWrapper>
                    <CM.FormItemLabel>类型</CM.FormItemLabel>
                </CM.FormItemLabelWrapper>
                <CM.FormItemContent>
                    {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                </CM.FormItemContent>
            </ListItem4ByecodeUi>
        </Group>
    )

    if (!miniProgramAccount) {
        return (
            <>
                {header}
                <UnitNoAuth type="WECHAT_MINI_PROGRAM" />
            </>
        )
    }

    return (
        <CM.Container>
            {header}
            <Divider />
            <Group label="运单信息">
                <CM.FieldList>
                    <CM.FieldItem>
                        <CM.FieldTitle required>openid</CM.FieldTitle>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            field={emptyTextField}
                            value={value.openid}
                            onBlur={openid => onChange({ ...value, openid })}
                            autoHeight
                            placeholder="支付者小程序 openid"
                            userOption={userOption}
                            upstreamOptions={textUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <CM.FieldTitle required>运单号</CM.FieldTitle>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            field={emptyTextField}
                            value={value.waybillId}
                            onBlur={waybillId => onChange({ ...value, waybillId })}
                            autoHeight
                            placeholder="请输入运单号"
                            userOption={userOption}
                            upstreamOptions={textUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <CM.FieldTitle required>微信支付交易单号</CM.FieldTitle>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            field={emptyTextField}
                            value={value.transId}
                            onBlur={transId => onChange({ ...value, transId })}
                            autoHeight
                            placeholder="一般以 420 开头"
                            userOption={userOption}
                            upstreamOptions={textUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <CM.FieldTitle required>收件人手机号</CM.FieldTitle>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            field={emptyTextField}
                            value={value.receiverPhone}
                            onBlur={receiverPhone => onChange({ ...value, receiverPhone })}
                            autoHeight
                            placeholder="请输入收件人手机号"
                            userOption={userOption}
                            upstreamOptions={textUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <CM.FieldTitle>寄件人手机号</CM.FieldTitle>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            field={emptyTextField}
                            value={value.senderPhone}
                            onBlur={senderPhone => onChange({ ...value, senderPhone })}
                            autoHeight
                            placeholder="可选"
                            userOption={userOption}
                            upstreamOptions={textUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <CM.FieldTitle required>订单详情路径</CM.FieldTitle>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            field={emptyTextField}
                            value={value.orderDetailPath}
                            onBlur={orderDetailPath => onChange({ ...value, orderDetailPath })}
                            autoHeight
                            placeholder="小程序订单详情页 path"
                            userOption={userOption}
                            upstreamOptions={textUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                    <DeliveryCompanyField
                        allParentNodes={allParentNodes}
                        label="运力 id"
                        placeholder="可选，填写可提高识别率"
                        value={value.deliveryId}
                        onChange={deliveryId => onChange({ ...value, deliveryId })}
                    />
                </CM.FieldList>
            </Group>
            <Divider />
            <Group label="商品信息">
                <CM.FieldList>
                    <SelectMultipleRecord
                        description="可选。从上游记录映射多件商品；不选则使用下方单件商品。"
                        value={value.goodsListNodeId}
                        onChange={goodsListNodeId => onChange({ ...value, goodsListNodeId })}
                        allParentNodes={allParentNodes}
                    />
                    <CM.FieldItem>
                        <CM.FieldTitle required>商品名称</CM.FieldTitle>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            field={emptyTextField}
                            value={value.goodsName}
                            onBlur={goodsName => onChange({ ...value, goodsName })}
                            autoHeight
                            placeholder="请输入商品名称"
                            userOption={userOption}
                            upstreamOptions={goodsTextUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <CM.FieldTitle required>商品图片</CM.FieldTitle>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            field={emptyTextField}
                            value={value.goodsImgUrl}
                            onBlur={goodsImgUrl => onChange({ ...value, goodsImgUrl })}
                            autoHeight
                            placeholder="请输入商品图片地址"
                            userOption={userOption}
                            upstreamOptions={goodsTextUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <CM.FieldTitle>商品描述</CM.FieldTitle>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            field={emptyTextField}
                            value={value.goodsDesc}
                            onBlur={goodsDesc => onChange({ ...value, goodsDesc })}
                            autoHeight
                            placeholder="可选"
                            userOption={userOption}
                            upstreamOptions={goodsTextUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                </CM.FieldList>
            </Group>
            <Divider />
            <OutputVariable nodeType={OtherPlatformNodeType.WECHAT_FOLLOW_WAYBILL} />
        </CM.Container>
    )
}
